import { useEffect, useRef, useState } from 'react';
import { Download, Copy, Check, X, ExternalLink, AlertTriangle, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { splitFootage, hasPullable, waitingWords } from '@/lib/tracking';
import { OK_KEY, clickAction, confirmedSince, afterNoConfirm } from '@/lib/footagePull';
import { EDITOR_MESSAGE } from '@/lib/autoprep';
import { FootagePullUpdateBox } from '@/components/FootagePullUpdate';

/**
 * The Download button on a project: opens Footage Pull with this project loaded, through
 * the footagepull:// scheme the app registers. Nobody types an ID.
 *
 * The scheme is fired from a hidden iframe (not by navigating the page). Whether the app
 * opened is CONFIRMED BY THE PORTAL, not guessed from window focus: the app tells the
 * portal "open, with this project" the moment it receives one, and the button polls
 * footage-status for a few seconds looking for that (or for a token issued after the
 * click). Window blur was the first design and it was wrong on macOS: the OS's own
 * "there is no application set to open the URL" dialog blurs the window exactly like a
 * launching app does.
 *
 * The scheme is only fired straight from the button in a browser where a hand-off has
 * worked before (lib/footagePull.js). Everywhere else the first click shows the setup
 * panel, with the downloads, and the panel's own button fires the scheme once the app is
 * installed: firing it first only got macOS's "no application" dialog, and the download
 * was never offered (Ryan, 2026-10-02). The panel reads as "get the app, here it is",
 * never as a failure.
 */
const SCHEME = 'footagepull';
const CONFIRM_WINDOW_MS = 8000;   // how long to wait for the app to say it opened
const CONFIRM_EVERY_MS = 1000;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
// Remembered per browser: once the hand-off has worked here, a click fires the scheme
// straight away and the install panel does not come back. A slow "Open Footage Pull?"
// browser prompt, or the app taking a moment to launch, used to re-show the whole install
// explanation to someone who has had the app for months. If a remembered hand-off does
// not confirm, the memory is stale (the app was removed): it is forgotten, and the quiet
// box offers the download right there.
export function handoffWorkedBefore() { try { return localStorage.getItem(OK_KEY) === '1'; } catch { return false; } }
function rememberHandoff() { try { localStorage.setItem(OK_KEY, '1'); } catch { /* private mode: nothing to remember */ } }
function forgetHandoff() { try { localStorage.removeItem(OK_KEY); } catch { /* private mode: nothing remembered */ } }
// The project's raw links, split the way the backend's project_urls does, for the quiet
// "show links" toggle: the browser fallback the guide describes, and the re-share asks.
const LINK_FIELDS = ['video_files_link', 'video_files_links', 'photo_files_link', 'photo_files_links', 'files_link'];
export function rawLinks(project) {
  // the project's download links, as the server's project_urls reads them: tracking a client put in a link box is
  // not one (lib/tracking.js), so "show links" never offers a carrier's tracking page; and once the team has set
  // "Our Dropbox copy" it is the only link (backend app/footage_copy.py), as it is for Footage Pull
  if ((project?.footage_copy_link || '').trim()) return [project.footage_copy_link.trim()];
  const out = [];
  for (const f of LINK_FIELDS) for (const u of splitFootage(project?.[f]).links) if (!out.includes(u)) out.push(u);
  return out;
}

// Product strings, exported so the "what's new" announcement shows the button as it is
// and cannot drift from it (announcements import product copy, never retype it).
export const BUTTON_LABEL = 'Download footage';
export const BUTTON_HINT = 'Opens Footage Pull with this project loaded.';
// Footage Pull is the only way editors get footage (Ryan, 2026-10-09): the "Download from NAS" (MediaShuttle) button a
// hard drive used to get is gone. A drive is uploaded to our Dropbox and pasted as "Our Dropbox copy", and this button
// pulls that like any other link.
// The "still uploading" warning. `project.still_uploading` is computed by the server on
// every read (app/footage_warning.py): the client's flag says the upload is in progress
// and no link check has listed the full declared size. It warns, it never hides the
// button, and it clears on its own. This sentence must match footage_warning.NOTE (a
// backend test reads this file to check).
export const STILL_UPLOADING_NOTE = 'The client marked this upload as still in progress, so a pull now may be incomplete.';

export function pullUrl(projectId) { return `${SCHEME}://pull/${projectId}`; }

export function detectPlatform() {
  const p = `${navigator.platform || ''} ${navigator.userAgent || ''}`;
  if (/Mac|iPhone|iPad/i.test(p)) return 'mac-universal';
  if (/Win/i.test(p)) return 'windows-x64';
  return null;
}

export function fireScheme(url) {
  let f = document.getElementById('footagepull-launcher');
  if (!f) {
    f = document.createElement('iframe'); f.id = 'footagepull-launcher'; f.style.display = 'none';
    f.setAttribute('aria-hidden', 'true'); document.body.appendChild(f);
  }
  f.src = url;
}

export default function FootagePullButton({ project, role, compact = false }) {
  const [view, setView] = useState('none');             // none | panel | quiet: the box under the button
  const [opening, setOpening] = useState(false);        // waiting for the app to confirm
  const [missed, setMissed] = useState(false);          // an open asked for from the box did not confirm
  const [builds, setBuilds] = useState(null);           // null = not asked yet
  const [copied, setCopied] = useState(false);
  const [showLinks, setShowLinks] = useState(false);
  const [copiedLink, setCopiedLink] = useState(-1);
  const [copiedPull, setCopiedPull] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);


  const loadBuilds = () => {
    if (builds !== null) return;
    fetch(`${BACKEND}/api/tools/footage_pull/version`).then(r => r.json())
      .then(d => setBuilds(d?.builds || {})).catch(() => setBuilds({}));
  };

  // Fire the scheme and wait for the app to confirm. `from` is where it was asked for: the main
  // 'button', the setup 'panel', or the quiet box's Try again (lib/footagePull.js afterNoConfirm).
  const openInApp = (from) => {
    if (opening) return;
    setOpening(true); setMissed(false); loadBuilds();
    const clickedAt = new Date().toISOString();
    fireScheme(pullUrl(project.id));
    const deadline = Date.now() + CONFIRM_WINDOW_MS;
    const check = async () => {
      try {
        const r = await api.get(`/projects/${project.id}/footage-status`);
        if (confirmedSince(r.data, clickedAt)) { rememberHandoff(); setOpening(false); setMissed(false); setView('none'); return; }
      } catch { /* a failed poll is no signal; keep waiting */ }
      if (Date.now() < deadline) { timer.current = setTimeout(check, CONFIRM_EVERY_MS); return; }
      const next = afterNoConfirm({ workedBefore: handoffWorkedBefore(), from });
      if (next.forget) forgetHandoff();
      setOpening(false); setMissed(next.missed); setView(next.view);
    };
    timer.current = setTimeout(check, CONFIRM_EVERY_MS);
  };
  const open = () => {
    if (opening) return;
    if (clickAction(handoffWorkedBefore()) === 'fire') { openInApp('button'); return; }
    loadBuilds(); setMissed(false); setView('panel');
  };
  const links = rawLinks(project);
  const copyLink = (u, i) => {
    (navigator.clipboard?.writeText(u) || Promise.reject())
      .then(() => { setCopiedLink(i); setTimeout(() => setCopiedLink(-1), 1500); })
      .catch(() => toast.error('Copy failed. Select the link and copy it manually.'));
  };

  // THE LINK TO PASTE (Ryan, 2026-10-28): an editor whose click did not open Footage Pull pasted the client's Google Drive
  // link into the app's "Project link or ID", the only link the portal showed him. The app takes this project's own
  // Footage Pull link (footagepull://pull/<id>: its window reads the project out of it), never the client's links, which
  // it reads itself once it has the project.
  const copyPullLink = () => {
    (navigator.clipboard?.writeText(pullUrl(project.id)) || Promise.reject())
      .then(() => { setCopiedPull(true); setTimeout(() => setCopiedPull(false), 2000); })
      .catch(() => toast.error('Copy failed. The project ID is on the Activity tab: paste that instead.'));
  };
  const pasteHelp = (
    <p className="text-[11px] text-slate-400 leading-relaxed" data-testid="footage-pull-paste-help">
      Button didn&apos;t open it? Open Footage Pull yourself, then paste this project&apos;s link into{' '}
      <strong className="text-zinc-200">Project link or ID</strong>:{' '}
      <button type="button" onClick={copyPullLink} data-testid="footage-pull-copy-pull-link"
        className="tap-auto inline-flex items-center gap-1 text-[11px] font-medium text-ug-gold hover:underline underline-offset-2">
        {copiedPull ? <><Check className="w-3 h-3 text-emerald-400" /> Copied</> : <><Copy className="w-3 h-3" /> Copy the Footage Pull link</>}
      </button>
      . Not the client&apos;s Google Drive, Dropbox or WeTransfer link: Footage Pull downloads those itself.
    </p>
  );

  const copyCommand = () => {
    const cmd = `py footage_pull.py ${project.id} "<destination folder>"`;
    (navigator.clipboard?.writeText(cmd) || Promise.reject())
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })
      .catch(() => toast.error('Copy failed. The project ID is on the Activity tab.'));
  };

  const mine = detectPlatform();
  const order = [mine, 'mac-universal', 'windows-x64'].filter((k, i, a) => k && a.indexOf(k) === i);
  const published = builds ? order.filter(k => builds[k]) : [];

  // The downloads, shown in the setup panel and in the quiet box (a remembered app that did not open).
  const downloads = (
    <>
      {builds === null && <p className="text-[11px] text-slate-500">Finding the download…</p>}
      {builds !== null && published.length === 0 && (
        <p className="text-[11px] text-slate-400" data-testid="footage-pull-fallback-none">
          The app is not published yet. For now, use the script: it is on the Editor Guide under &ldquo;Prefer the terminal?&rdquo;, and the command to run it is below.
        </p>
      )}
      {published.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="footage-pull-fallback-downloads">
          {published.map((k, i) => {
            const b = builds[k]; const mb = b.size ? ` · ${Math.round(b.size / 1048576)} MB` : '';
            return (
              <a key={k} href={`${BACKEND}${b.url}`} data-testid={`footage-pull-download-${k}`}
                className={`tap-auto inline-flex items-center gap-1.5 text-[11px] font-medium rounded-md px-2.5 py-1.5 border transition-colors ${
                  i === 0 ? 'text-ug-gold border-ug-gold/40 bg-ug-gold/15 hover:bg-ug-gold/20' : 'text-slate-300 border-ug-border hover:text-zinc-100'}`}>
                <Download className="w-3.5 h-3.5" /> Download for {b.label}{mb}
              </a>
            );
          })}
        </div>
      )}
    </>
  );

  // The project's links, for the quiet "show links" toggle: said to be the client's, which Footage Pull downloads itself,
  // so nobody pastes one into its "Project link or ID" (THE LINK TO PASTE, above).
  const linksList = showLinks && links.length > 0 && (
    <>
    <p className="mt-2 text-[10px] text-slate-500" data-testid="footage-pull-links-note">
      The client&apos;s links. Footage Pull downloads these itself: don&apos;t paste them into Footage Pull.
    </p>
    <ul className="mt-1 space-y-1" data-testid="footage-pull-links">
      {links.map((u, i) => (
        <li key={u} className="flex items-center gap-2 min-w-0">
          <a href={u.startsWith('http') ? u : `https://${u}`} target="_blank" rel="noopener noreferrer"
            className="text-[11px] text-oxidized-teal hover:text-oxidized-teal/80 underline underline-offset-2 break-all">{u}</a>
          <button type="button" onClick={() => copyLink(u, i)} aria-label="Copy link"
            className="tap-auto shrink-0 p-0.5 rounded text-slate-500 hover:text-zinc-100">
            {copiedLink === i ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
          </button>
        </li>
      ))}
    </ul>
    </>
  );

  // The button shown OFF: it is there, it cannot be pressed, and the words under it say why (Ryan, 2026-10-12: "the
  // download footage button should be grayed out if it doesnt work right now until we upload our db link and autoprep
  // is ready"). Two states draw it, below.
  const offButton = (
    <button type="button" disabled aria-disabled="true" data-testid="footage-pull-open" title="Nothing to download yet"
      className="ug-cta tap-auto font-sans text-sm shadow-none opacity-40 grayscale cursor-not-allowed">
      <span className="ug-cta-inner"><Download className="w-4 h-4" /> {BUTTON_LABEL}</span>
    </button>
  );

  // NOTHING TO PULL YET. No link on the job is one Footage Pull can take: a hard drive not yet brought in, or footage at
  // any link but Dropbox, Google Drive or WeTransfer, and no Our Dropbox copy. The button is shown off and turns on by
  // itself when a link it can take reaches the job. The team keeps "show links" (that is where the footage to bring in
  // by hand is); an editor does not, since nothing there is theirs to download. Tracking a client put in a link box
  // never counts (lib/tracking.js). A hard-drive job with nothing to pull showed no button at all from 2026-09-29; it
  // now shows this.
  if (!hasPullable(project)) {
    return (
      <div data-testid="footage-pull" data-state="waiting" className={compact ? '' : 'mb-4'}>
        <div className="flex items-center gap-2 flex-wrap">
          {offButton}
          {role !== 'editor' && links.length > 0 && (
            <button type="button" onClick={() => setShowLinks(v => !v)} data-testid="footage-pull-show-links"
              className="tap-auto ml-auto text-[10px] text-slate-500 hover:text-zinc-100 underline underline-offset-2 transition-colors">
              {showLinks ? 'hide links' : 'show links'}
            </button>
          )}
        </div>
        {!compact && (
          <p className="mt-1.5 text-[11px] text-slate-400 leading-relaxed" data-testid="footage-pull-waiting">{waitingWords(project, role)}</p>
        )}
        {role !== 'editor' && linksList}
      </div>
    );
  }

  // R3 (docs/autoprep/QUEUE.md): while auto-prep holds the order an editor cannot download yet, and is told why, here,
  // instead of meeting the token route's refusal in Footage Pull. The team keeps the button. The editor sees the button
  // too, off, above the reason (2026-10-12): it is where the download will be, and it turns on when the hold ends.
  if (role === 'editor' && project?.autoprep?.held) {
    return (
      <div data-testid="footage-pull" data-state="held" className={compact ? '' : 'mb-4'}>
        {!compact && <div className="mb-2">{offButton}</div>}
        <p data-testid="footage-pull-held" className="tap-auto inline-flex items-start gap-1.5 text-[11px] leading-relaxed rounded-md border px-3 py-2 text-oxidized-teal border-oxidized-teal/30 bg-oxidized-teal/[0.07]">
          <Wand2 className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {EDITOR_MESSAGE}
        </p>
      </div>
    );
  }

  return (
    <div data-testid="footage-pull" className={compact ? '' : 'mb-4'}>
      {role === 'editor' && !compact && <FootagePullUpdateBox />}
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={open} disabled={opening} data-testid="footage-pull-open"
          className="ug-cta tap-auto font-sans text-sm shadow-none disabled:opacity-70">
          <span className="ug-cta-inner"><Download className="w-4 h-4" /> {opening ? 'Opening Footage Pull…' : BUTTON_LABEL}</span>
        </button>
        <span className="text-[10px] text-slate-500">{BUTTON_HINT}</span>
        {links.length > 0 && (
          <button type="button" onClick={() => setShowLinks(v => !v)} data-testid="footage-pull-show-links"
            className="tap-auto ml-auto text-[10px] text-slate-500 hover:text-zinc-100 underline underline-offset-2 transition-colors">
            {showLinks ? 'hide links' : 'show links'}
          </button>
        )}
      </div>

      {project?.still_uploading && (
        <p className="mt-1.5 text-[11px] text-amber-300 leading-relaxed flex items-start gap-1.5" data-testid="footage-pull-still-uploading" data-tone="warning">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>{STILL_UPLOADING_NOTE} Pull now if you need to start, and pull again once they finish: files already verified are never re-downloaded.</span>
        </p>
      )}

      {linksList}

      {view === 'quiet' && (
        <div className="mt-2 space-y-2" data-testid="footage-pull-quiet">
          <p className="text-[11px] text-slate-400">
            Footage Pull didn&apos;t open. If it isn&apos;t on this computer any more, download it again:
          </p>
          {downloads}
          <p className="text-[11px] text-slate-400" data-testid="footage-pull-quiet-hint">
            {missed && <span className="text-amber-300" data-testid="footage-pull-quiet-missed">It still didn&apos;t open. </span>}
            Still installed? Open it once by hand from where you installed it, then click{' '}
            <button type="button" onClick={() => openInApp('quiet')} disabled={opening} data-testid="footage-pull-quiet-retry"
              className="tap-auto text-ug-gold hover:underline underline-offset-2 disabled:opacity-70">{opening ? 'Opening…' : 'Try again'}</button>.{' '}
            <button type="button" onClick={() => { setMissed(false); setView('panel'); }} data-testid="footage-pull-setup-again"
              className="tap-auto text-slate-500 hover:text-zinc-100 underline underline-offset-2">Set it up again</button>
          </p>
          {pasteHelp}
        </div>
      )}

      {view === 'panel' && (
        <div data-testid="footage-pull-fallback" className="mt-3 rounded-xl border border-ug-gold/25 bg-ug-gold/[0.05] p-4 relative">
          <button type="button" onClick={() => { setMissed(false); setView('none'); }} aria-label="Close" data-testid="footage-pull-fallback-close"
            className="tap-auto absolute right-2 top-2 p-1 rounded text-slate-500 hover:text-zinc-100"><X className="w-3.5 h-3.5" /></button>
          <h4 className="font-sans text-[12px] font-semibold text-zinc-100 pr-6">Footage Pull downloads this project&apos;s footage for you</h4>
          <p className="text-[11px] text-slate-400 leading-relaxed mt-1">
            Footage Pull is a small app that downloads a project&apos;s footage no matter how it was sent (Dropbox, Google Drive or WeTransfer).
            Once it is on your computer, this button opens it with the project already loaded. Four steps, once.
          </p>

          <p className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/80 mt-3 mb-1.5">1. Get the app</p>
          {downloads}

          <p className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/80 mt-3 mb-1.5">2. Open it once</p>
          <p className="text-[11px] text-slate-400 leading-relaxed">
            Open Footage Pull once from where you put it, so your computer learns to hand projects to it.
            The first open asks you to confirm. <strong className="text-zinc-200">Windows:</strong> on
            &ldquo;Windows protected your PC&rdquo;, click <strong className="text-zinc-200">More info</strong>, then <strong className="text-zinc-200">Run anyway</strong>.
            <strong className="text-zinc-200"> Mac:</strong> when it asks if you are sure you want to open it, click <strong className="text-zinc-200">Open</strong>.
            The full steps are in the <a href="/editor/guide" className="text-ug-gold hover:underline inline-flex items-center gap-0.5">Editor Guide <ExternalLink className="w-3 h-3" /></a>.
          </p>

          <p className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/80 mt-3 mb-1.5">3. Open this project in it</p>
          <button type="button" onClick={() => openInApp('panel')} disabled={opening} data-testid="footage-pull-fallback-open"
            className="tap-auto inline-flex items-center gap-1.5 text-[11px] font-medium rounded-md px-2.5 py-1.5 border text-ug-gold border-ug-gold/40 bg-ug-gold/15 hover:bg-ug-gold/20 transition-colors disabled:opacity-70">
            <ExternalLink className="w-3.5 h-3.5" /> {opening ? 'Opening Footage Pull…' : <>I&apos;ve installed it: open this project in Footage Pull</>}
          </button>
          {missed && (
            <p className="mt-1.5 text-[11px] text-amber-300 leading-relaxed" data-testid="footage-pull-fallback-missed">
              Footage Pull didn&apos;t open. Open it once by hand from where you installed it, then click the button again.
            </p>
          )}
          <p className="mt-1.5 text-[11px] text-slate-400 leading-relaxed">
            If your browser asks whether to open Footage Pull, choose <strong className="text-zinc-200">Allow</strong> (and &ldquo;always&rdquo; if it offers).
          </p>
          <div className="mt-1.5">{pasteHelp}</div>

          {/* Ryan, 2026-10-01: an editor was asked for an email and password with no warning, by a box naming
              backend.uncutgems.com, an address no editor has seen. The app signs in as the editor (app/footage_pull.py
              keeps the session in ~/.uncutgems), so say so here, before it asks. */}
          <p className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/80 mt-3 mb-1.5">4. Sign in once</p>
          <p className="text-[11px] text-slate-400 leading-relaxed" data-testid="footage-pull-login-step">
            The first time, Footage Pull asks for your <strong className="text-zinc-200">portal email and password</strong>: the
            same ones you use to sign in here. The box says <strong className="text-zinc-200">backend.uncutgems.com</strong>,
            which is our portal&apos;s server, so it is safe to enter them. It remembers you after that, so you only do this
            once on each computer.
          </p>

          <div className="mt-3 pt-3 border-t border-ug-border-subtle flex items-center gap-2 flex-wrap">
            <span className="text-[10px] text-slate-500">Prefer the terminal?</span>
            <button type="button" onClick={copyCommand} data-testid="footage-pull-copy-command"
              className="tap-auto inline-flex items-center gap-1 text-[10px] font-medium text-slate-400 hover:text-zinc-100 border border-ug-border rounded px-2 py-1 transition-colors">
              {copied ? <><Check className="w-3 h-3 text-emerald-400" /> Copied</> : <><Copy className="w-3 h-3" /> Copy the command</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
